"use client";

import { useQuery } from "@tanstack/react-query";
import { useParams } from "next/navigation";
import { use } from "react";
import { LuCircleX as LuXCircle } from "react-icons/lu";

import { getEarningsRequest } from "@/backend/focus";
import { getEarningsByDay } from "@/backend/focus/admin-ui";
import { TimePeriod } from "@/components/app/discover-sort-constants";
import {
  NOTIFICATION_FIELDS,
  useNotificationsContext,
} from "@/components/app/notifications/notifications-context";
import AnimatedMetric from "@/components/shared/animated-metric";
import { EarningsGraph } from "@/components/shared/earnings-graphs";
import { InfoTooltip } from "@/components/shared/info-tooltip";
import { Metric } from "@/components/shared/metric";
import { TextDivider } from "@/components/shared/text-divider";
import UserSearch from "@/components/shared/users-search/user-search";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import type { FilterNumericValue } from "@/contexts/filters-sort-context";
import {
  FilterType,
  TimePeriodLabels,
  TimePeriodMap,
} from "@/contexts/filters-sort-context";
import { UserContext } from "@/contexts/user-context";
import { cn } from "@/utils/lib";

import { EarningsCategories } from "./constants";
import { EarningsPieChart } from "./earnings-pie-chart";

export const EarningsFilterHeader = () => {
  const { currentUserPublicKey } = use(UserContext);
  const { id: username } = useParams();

  const {
    onFiltersApply,
    updateFilter,
    findFilterByName,
    removeFilter,
    notificationPublicKey,
  } = useNotificationsContext();
  const currentCategory = findFilterByName(
    NOTIFICATION_FIELDS.subcategory,
  )?.value;
  const currentPeriodValue =
    findFilterByName(NOTIFICATION_FIELDS.afterDaysAgo)?.value ||
    TimePeriodMap.AllTime;
  const currentPeriod =
    TimePeriodMap[currentPeriodValue as string as keyof typeof TimePeriodMap];
  const categoryMeta =
    EarningsCategories?.[currentCategory as keyof typeof EarningsCategories];

  const { data: earningsData, isLoading: earningsDataLoading } = useQuery({
    queryFn: () =>
      getEarningsRequest({
        afterDaysAgo:
          currentPeriod === TimePeriod.AllTime
            ? undefined
            : +currentPeriodValue,
        publicKey: notificationPublicKey,
      }),
    queryKey: [
      "earnings",
      currentPeriod,
      currentPeriodValue,
      notificationPublicKey,
    ],
  });
  const { data: earningsByDayData, isLoading: earningsByDayLoading } = useQuery(
    {
      queryFn: () =>
        getEarningsByDay({
          afterDaysAgo:
            currentPeriod === TimePeriod.AllTime
              ? undefined
              : +currentPeriodValue,
          subcategory: currentCategory as string,
          publicKey: notificationPublicKey,
          currentUserPublicKey: currentUserPublicKey,
        }),
      queryKey: [
        "earnings-by-day",
        currentPeriod,
        currentCategory,
        currentPeriodValue,
        currentUserPublicKey,
        notificationPublicKey,
      ],
    },
  );

  const handleMetricClick = (category: string) => () => {
    if (category === currentCategory) {
      removeFilter(NOTIFICATION_FIELDS.subcategory);
    } else {
      updateFilter({
        name: NOTIFICATION_FIELDS.subcategory,
        type: FilterType.string,
        value: category,
      });
    }
    onFiltersApply();
  };

  const handleDaysAgoChange = (newPeriod: keyof typeof TimePeriodMap) => {
    if (newPeriod === TimePeriod.AllTime) {
      removeFilter(NOTIFICATION_FIELDS.afterDaysAgo);
    } else {
      updateFilter({
        name: NOTIFICATION_FIELDS.afterDaysAgo,
        type: FilterType.numeric,
        value: TimePeriodMap[newPeriod] as FilterNumericValue,
      });
    }
    onFiltersApply();
  };

  return (
    <>
      <div
        className="
        flex w-full justify-center border-b border-border-light p-4
      "
      >
        <h1
          className="
            flex flex-wrap items-center gap-2 font-sans text-xl font-semibold
          "
        >
          Earnings {username && <span>for @{username}</span>}
        </h1>
        <div className="ml-auto">
          <UserSearch
            placeholder="Search any user..."
            baseRedirectUrl="/earnings/"
          />
        </div>
      </div>
      <div
        className="
        flex w-full justify-center border-b border-border-light p-4
      "
      >
        <h1
          className="
            flex flex-wrap items-center gap-2 font-sans text-xl font-semibold
          "
        >
          <span
            className={cn(
              `font-semibold`,
              categoryMeta?.color ?? "text-muted-foreground",
            )}
          >
            {categoryMeta?.label ?? "All Sources"}
          </span>
          {categoryMeta && (
            <InfoTooltip text="Clear type filter">
              <Button
                variant="ghost"
                size="sm"
                className="p-1"
                onClick={handleMetricClick(currentCategory as string)}
              >
                <LuXCircle className="text-lg" />
              </Button>
            </InfoTooltip>
          )}
        </h1>
        <div className="ml-auto">
          <Select
            value={currentPeriod as string}
            onValueChange={handleDaysAgoChange as (value: string) => void}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select period" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(TimePeriodLabels).map(([key, label]) => (
                <SelectItem key={key} value={key}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="w-full border-b border-border-light">
        <EarningsGraph
          height={160}
          earnings={earningsByDayData}
          loading={earningsByDayLoading}
          hideCharts={
            !!currentCategory &&
            !earningsData?.EarningsUsdCents?.[currentCategory as string]
          }
          colors={
            currentCategory
              ? [`var(--${categoryMeta?.color.replace("text-", "")})`]
              : undefined
          }
        />
      </div>

      <div className="flex flex-col gap-2 border-b border-border-light p-4">
        <div
          className="
            flex w-full flex-col gap-4
            sm:flex-row
          "
        >
          <div
            className="
              grid grow grid-cols-1 gap-4
              sm:grid-cols-2
              lg:grid-cols-2
              xl:grid-cols-3
            "
          >
            <div
              className="
                flex max-h-[188px] flex-col items-center
                sm:row-span-2
              "
            >
              {!earningsDataLoading && (
                <EarningsPieChart
                  earningsData={earningsData?.EarningsUsdCents}
                />
              )}
              {earningsDataLoading && (
                <Skeleton
                  className="
                    rounded-force aspect-square h-full max-h-[188px]
                    rounded-full bg-card
                  "
                />
              )}
            </div>
            {Object.entries(EarningsCategories).map(([key, category]) => {
              if ("comingSoon" in category && category.comingSoon) {
                return null;
              }
              return (
                <div
                  key={key}
                  className="cursor-pointer"
                  onClick={handleMetricClick(key)}
                >
                  <AnimatedMetric
                    className="cursor-pointer"
                    value={(earningsData?.EarningsUsdCents[key] ?? 0) / 100}
                    state={currentCategory === key ? "active" : "none"}
                    label={category.label}
                    color={category.color}
                    numDecimals={2}
                    isUsd={true}
                    loading={earningsDataLoading}
                  />
                </div>
              );
            })}
          </div>
        </div>
        <TextDivider text="Coming soon" spacing="my-4" />
        <div
          className="
            grid w-full grid-cols-1 gap-3
            sm:grid-cols-2
            lg:grid-cols-2
            xl:grid-cols-3
          "
        >
          {Object.entries(EarningsCategories).map(([key, category]) => {
            if ("comingSoon" in category && category.comingSoon) {
              return (
                <div key={key}>
                  <Metric
                    value="Coming soon"
                    state="none"
                    label={category.label}
                    color={category.color}
                  />
                </div>
              );
            }
            return null;
          })}
        </div>
      </div>
    </>
  );
};
